import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {catchError, Observable} from 'rxjs';
import {
  IAdxErpOutboundQueueMsgResponse,
  IUpdateAdxInvoiceErpPropsRequest,
} from '../interfaces/adx-erp-panel.interface';

/**
 * Lets a user manually close out the ADX -> ERP send for an invoice when the real ERP outbound
 * pipeline can't run (e.g. non-prod environments where ERP outbound is disabled). Mirrors the
 * two calls the real outbound pipeline makes automatically after a real send: stamping the
 * invoice with an ERP message id/timestamp, then recording the ERP response against it.
 */
@Injectable({
  providedIn: 'root',
})
export class AdxErpManualUpdateService {
  private readonly http = inject(HttpClient);

  updateErpProps(payload: IUpdateAdxInvoiceErpPropsRequest): Observable<void> {
    return this.http.put<void>('dbov2/adx-invoice/update-adx-invoice-erp-props', payload)
      .pipe(catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.detail || error?.message || 'Server error');
      }));
  }

  upsertErpResponse(payload: IAdxErpOutboundQueueMsgResponse): Observable<void> {
    // Dedicated manual-only endpoint -- kept separate from the automated ERP outbound pipeline's
    // endpoint so this admin action can require AdxInvoiceId without affecting that pipeline.
    return this.http.put<void>('dbov2/adx-invoice-erp-responses/manual', payload)
      .pipe(catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.detail || error?.message || 'Server error');
      }));
  }

  /**
   * Soft-deletes an existing AdxInvoiceErpResponse row. Used to let a user correct a mistake in
   * a previous manual (or real) entry: Upsert only truly overwrites an existing row when its
   * Result is still "PENDING" — once it's SUCCESS/ERROR, resubmitting with the same ErpMessageId
   * would insert a second row instead of fixing the first. Deleting the old row first, then
   * saving again, guarantees a clean edit regardless of its prior Result.
   */
  deleteErpResponse(adxInvoiceErpResponseId: number): Observable<void> {
    return this.http.delete<void>('dbov2/adx-invoice-erp-responses', {
      params: {AdxInvoiceErpResponseId: adxInvoiceErpResponseId},
    }).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.error?.detail || error?.message || 'Server error');
    }));
  }
}
